import { z } from 'zod';
export const PERMISSIONS = [
    'company:read',
    'company:manage',
    'users:read',
    'users:manage',
    'customers:read',
    'customers:write',
    'products:read',
    'products:write',
    'documents:read',
    'documents:issue',
    'documents:void',
    'receptions:read',
    'receptions:manage',
    'expenses:read',
    'expenses:write',
    'quotes:read',
    'quotes:write',
    'payments:read',
    'payments:write',
    'reports:read',
    'settings:manage',
    // Módulo Proyectos
    'projects:read',
    'projects:write',
    'projects:allocate',
    'payroll:read',
    'payroll:write',
    'payroll:settle',
];
export const permission = z.enum(PERMISSIONS);
export const ROLE_KEYS = ['owner', 'admin', 'accountant', 'seller', 'viewer'];
export const roleKey = z.enum(ROLE_KEYS);
const readOnly = PERMISSIONS.filter((p) => p.endsWith(':read'));
/**
 * Planes comerciales. El plan recorta lo que cualquier usuario puede hacer en la compañía, sea cual sea su rol:
 * - full: facturación completa desde la aplicación y por API.
 * - api: la compañía no factura desde la aplicación; sus sistemas emiten por la API y Facture firma, envía a
 *   Hacienda y manda el correo. Sin emisión manual, cotizaciones, cobros ni productos.
 */
export const COMPANY_PLANS = [
    { code: 'full', name: 'Plan full', description: 'Facturación completa desde la aplicación y por API.' },
    {
        code: 'api',
        name: 'Plan API',
        description: 'Solo por API: Facture firma, envía a Hacienda y manda el correo; no se factura desde la aplicación.',
    },
];
export const companyPlan = z.enum(['full', 'api']);
const API_PLAN_REMOVED = [
    'documents:issue',
    'documents:void',
    'quotes:read',
    'quotes:write',
    'payments:read',
    'payments:write',
    'products:read',
    'products:write',
];
/** Permisos efectivos: los del rol, recortados por el plan de la compañía. */
export function planPermissions(plan, granted) {
    if (plan === 'api')
        return granted.filter((p) => !API_PLAN_REMOVED.includes(p));
    return [...granted];
}
/** Roles de sistema que se crean con cada compañía. */
export const SYSTEM_ROLES = {
    owner: {
        name: 'Propietario',
        description: 'Control total de la compañía, incluida la facturación del plan.',
        permissions: PERMISSIONS,
    },
    admin: {
        name: 'Administrador',
        description: 'Gestiona usuarios, configuración de Hacienda y todos los módulos.',
        permissions: PERMISSIONS,
    },
    accountant: {
        name: 'Contador',
        description: 'Consulta todo, gestiona comprobantes recibidos, gastos y reportes.',
        permissions: [
            ...readOnly,
            'receptions:manage',
            'expenses:write',
            'payments:write',
            'documents:void',
            'projects:allocate',
            'payroll:settle',
        ],
    },
    seller: {
        name: 'Vendedor',
        description: 'Emite comprobantes y cotizaciones, gestiona clientes.',
        permissions: [
            'company:read',
            'customers:read',
            'customers:write',
            'products:read',
            'documents:read',
            'documents:issue',
            'quotes:read',
            'quotes:write',
            'payments:read',
            'payments:write',
        ],
    },
    viewer: {
        name: 'Solo lectura',
        description: 'Consulta sin modificar.',
        permissions: readOnly,
    },
};
/* ------------------------------------------------------------------ */
/* Módulos de la plataforma                                            */
/* ------------------------------------------------------------------ */
/**
 * Módulos de Facture. Los `core` están en toda compañía; los demás los activa el superadmin por compañía
 * (Administración → Compañías → Módulos). Un permiso de un módulo no activo no rige para nadie de esa compañía,
 * sea cual sea su rol. Cada módulo nuevo se agrega aquí con sus permisos (acciones).
 */
export const APP_MODULES = [
    {
        key: 'general',
        name: 'General',
        description: 'Compañía, usuarios, configuración y reportes.',
        core: true,
        permissions: [
            'company:read',
            'company:manage',
            'users:read',
            'users:manage',
            'settings:manage',
            'reports:read',
        ],
    },
    {
        key: 'billing',
        name: 'Facturación',
        description: 'Comprobantes electrónicos, cotizaciones, cobros, recepción, gastos, clientes y productos.',
        core: true,
        permissions: [
            'customers:read',
            'customers:write',
            'products:read',
            'products:write',
            'documents:read',
            'documents:issue',
            'documents:void',
            'receptions:read',
            'receptions:manage',
            'expenses:read',
            'expenses:write',
            'quotes:read',
            'quotes:write',
            'payments:read',
            'payments:write',
        ],
    },
    {
        key: 'projects',
        name: 'Proyectos',
        description: 'Obras y proyectos: presupuesto, costos imputados desde la recepción de facturas y gastos, personal y planilla.',
        core: false,
        permissions: [
            'projects:read',
            'projects:write',
            'projects:allocate',
            'payroll:read',
            'payroll:write',
            'payroll:settle',
        ],
    },
];
export const appModuleKey = z.enum(APP_MODULES.map((m) => m.key));
/** Módulos que se activan por compañía (no core). */
export const OPTIONAL_MODULES = APP_MODULES.filter((m) => !m.core).map((m) => m.key);
/** Módulos activos de una compañía: los core más los opcionales que tiene encendidos (en el orden del catálogo). */
export function activeModules(enabledModules) {
    const enabled = new Set(enabledModules ?? []);
    return APP_MODULES.filter((m) => m.core || enabled.has(m.key)).map((m) => m.key);
}
/** Nombre y explicación de cada acción, para el editor de permisos por usuario. */
export const PERMISSION_INFO = {
    'company:read': { label: 'Ver la compañía', description: 'Datos generales de la compañía.' },
    'company:manage': { label: 'Editar la compañía', description: 'Datos, logo, sucursales y terminales.' },
    'users:read': { label: 'Ver el equipo', description: 'Usuarios de la compañía y sus roles.' },
    'users:manage': {
        label: 'Gestionar el equipo',
        description: 'Invitar, quitar y cambiar permisos de usuarios.',
    },
    'settings:manage': {
        label: 'Configuración',
        description: 'Hacienda, correo, API de integración y consecutivos.',
    },
    'reports:read': { label: 'Reportes', description: 'Reportes y exportaciones.' },
    'customers:read': { label: 'Ver clientes', description: 'Clientes y proveedores.' },
    'customers:write': { label: 'Editar clientes', description: 'Crear, editar e importar clientes.' },
    'products:read': { label: 'Ver productos', description: 'Catálogo de productos y servicios.' },
    'products:write': { label: 'Editar productos', description: 'Crear, editar, importar y fijar precios.' },
    'documents:read': { label: 'Ver comprobantes', description: 'Facturas, tiquetes y notas.' },
    'documents:issue': { label: 'Emitir comprobantes', description: 'Crear y enviar a Hacienda.' },
    'documents:void': { label: 'Anular comprobantes', description: 'Notas de crédito de anulación.' },
    'receptions:read': { label: 'Ver recepción', description: 'Comprobantes recibidos de proveedores.' },
    'receptions:manage': { label: 'Responder recepción', description: 'Aceptar o rechazar ante Hacienda.' },
    'expenses:read': { label: 'Ver gastos', description: 'Gastos y categorías.' },
    'expenses:write': { label: 'Editar gastos', description: 'Registrar y editar gastos y adjuntos.' },
    'quotes:read': { label: 'Ver cotizaciones', description: 'Cotizaciones y planes recurrentes.' },
    'quotes:write': { label: 'Editar cotizaciones', description: 'Crear, convertir y ejecutar planes.' },
    'payments:read': { label: 'Ver cobros', description: 'Cobros y cuentas por cobrar.' },
    'payments:write': { label: 'Registrar cobros', description: 'Cobros, recordatorios y estados de cuenta.' },
    'projects:read': { label: 'Ver proyectos', description: 'Proyectos, su resumen, costos y artículos.' },
    'projects:write': {
        label: 'Gestionar proyectos',
        description: 'Crear y editar proyectos, presupuesto y partidas.',
    },
    'projects:allocate': {
        label: 'Imputar costos',
        description: 'Asignar facturas recibidas y gastos a proyectos (desde Recepción o Gastos).',
    },
    'payroll:read': {
        label: 'Ver personal y planilla',
        description: 'Trabajadores, puestos, horas registradas, hoja semanal y saldos por pagar.',
    },
    'payroll:write': {
        label: 'Registrar horas',
        description: 'Registrar y editar horas, adelantos, dietas y transporte; gestionar trabajadores, puestos y asignaciones.',
    },
    'payroll:settle': {
        label: 'Liquidar planilla',
        description: 'Liquidar periodos: guardar borradores, cerrar (pagar saldos) y revertir el último cierre.',
    },
};
/** Módulo al que pertenece cada permiso. */
export function moduleOfPermission(p) {
    return (APP_MODULES.find((m) => m.permissions.includes(p))?.key ??
        'general');
}
/** Quita los permisos de módulos opcionales que la compañía no tiene activos. */
export function modulePermissions(enabledModules, granted) {
    const enabled = new Set(enabledModules ?? []);
    return granted.filter((p) => {
        const mod = APP_MODULES.find((m) => m.key === moduleOfPermission(p));
        return !mod || mod.core || enabled.has(mod.key);
    });
}
/**
 * Permisos efectivos de un usuario en una compañía: los propios del usuario si el administrador los ajustó
 * (`custom`), si no los de su rol; recortados por el plan y por los módulos activos de la compañía.
 */
export function effectivePermissions(input) {
    const base = input.custom ?? input.role;
    return modulePermissions(input.modules, planPermissions(input.plan, base));
}
//# sourceMappingURL=permissions.js.map